import { useEffect, useRef, useState } from "react";
import { PleasewaitPopup } from "./PleasewaitPopup";
import { post_EnrollmentClass } from "../services/Class";
import useAuth from "../hook/useAuth";
import EmailValidatedToCheckExistComponent from "./EmailValidatedToCheckExistComponent";
import { DoesEmailExist, DoesUserNameExist, getUserByEmail, post_User } from "../services/Users";
import UsernameValidatedComponent from "./UsernameValidatedComponent";
import { onChangePhoneNumber } from "../utils/phoneNumberRegex";
import PasswordValidation from "./PasswordValidation";

type AddStudentProps = {
    showPopup: boolean;
    setShowPopup: (value: boolean) => void;
    classId: number;
    setRefreshClassList: (value: boolean) => void;
}

export const AddStudent = ({ showPopup, setShowPopup, classId, setRefreshClassList }: AddStudentProps) => {
    const { auth }: any = useAuth();
    const [showPleasewait, setShowPleasewait] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [isStudentHaveAccount, setIsStudentHaveAccount] = useState<boolean>(true);
    const [email, setEmail] = useState<string>("");
    const [emailIsValid, setEmailIsValid] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [userName, setUserName] = useState<string>("");
    const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
    const [isUserNameExist, setIsUserNameExist] = useState<boolean | null>(null);
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [isEmailExist, setIsEmailExist] = useState<boolean | null>(null);
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [isStudentInfoValid, setIsStudentInfoValid] = useState<boolean>(false);

    useEffect(() => {
        if (showPopup) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [showPopup]);


    useEffect(() => {
        if (firstName.trim() && lastName.trim() && emailAddress.trim() && phoneNumber.trim() && password.trim()) {
            const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(emailAddress.trim());
            const isValidPassword =
                password.trim().length >= 8 &&
                /\d/.test(password.trim()) &&
                /[a-z]/.test(password.trim()) &&
                /[A-Z]/.test(password.trim()) &&
                /[!@#$%^&*(),.?":{}|<>]/.test(password.trim());
            if (isValid && isValidPassword && !isUserNameExist && !isEmailExist) {
                setIsStudentInfoValid(true)
            }
            else {
                setIsStudentInfoValid(false)
            }
        }
        else {
            setIsStudentInfoValid(false);
        }
    }, [firstName, lastName, emailAddress, phoneNumber, password, isEmailExist, isUserNameExist])



    if (!showPopup) return null;

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(value);
        e.target.setCustomValidity(isValid ? "" : "Please enter a valid email");
        setEmailIsValid(isValid);
    };

    const isInvalid = () => {
        if (currentStep === 1) return false;
        if (currentStep === 2 && isStudentHaveAccount && emailIsValid) return false;
        if (currentStep === 2 && !isStudentHaveAccount && isStudentInfoValid) return false;

        return true;
    }


    const handleNextClick = async () => {
        if (currentStep === 1) {
            setCurrentStep(2);
        }
        else if (currentStep === 2 && isStudentHaveAccount && email.trim()) {
            setShowPleasewait(true);
            getUserByEmail(email, 0, 1)
                .then((data) => {
                    if (data.count === 1 && data.userReport?.length === 1) {
                        post_EnrollmentClass({
                            studentId: data.userReport[0]?.id,
                            classId,
                            emailAddress: "",
                            status: "Pending",
                            enrolledAt: null,
                            createdAt: new Date().toISOString(),
                            createdBy: auth?.id
                        }, auth?.id)
                            .then((res) => {
                                setShowPleasewait(false);
                                setShowPopup(false);
                                setRefreshClassList(true);
                            })
                    }
                    else {
                        post_EnrollmentClass({
                            studentId: 0,
                            classId,
                            emailAddress: email,
                            status: "Pending",
                            enrolledAt: null,
                            createdAt: new Date().toISOString(),
                            createdBy: auth?.id
                        }, auth?.id)
                            .then((res) => {
                                setShowPleasewait(false);
                                setShowPopup(false);
                                setRefreshClassList(true);
                            })
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
        }
        else if (currentStep === 2 && !isStudentHaveAccount) {
            setShowPleasewait(true);
            post_User({
                firstName,
                lastName,
                userName,
                emailAddress,
                mobilPhoneNumber: phoneNumber,
                password: password,
                forcePasswordChange: true,
                isActive: true,
                dateFormat: "MM/dd/yyyy",
                language: "English",
                bio: "",
                profilePic: "",
                roleId: 5,
                lastLogin: "",
                createdAt: new Date(),
                createdBy: auth?.givenname,
                updatedAt: "",
                updatedBy: ""
            })
                .then((id: number) => {
                    post_EnrollmentClass({
                        studentId: id,
                        classId,
                        emailAddress: "",
                        status: "Pending",
                        createdAt: new Date().toISOString(),
                        createdBy: auth?.id
                    }, auth?.id)
                        .then((res) => {
                            setShowPleasewait(false);
                            setShowPopup(false);
                            setRefreshClassList(true);
                        })
                })
                .catch((error) => {
                    console.error(error);
                })
        }
    }

    const handleBackClick = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        }
    }

    return (
        <>
            <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50 p-10">
                <div className="bg-white w-full max-w-4xl rounded-md shadow-lg">
                    <div className="px-6 py-4 border-b w-full flex justify-between items-center">
                        <h2 className="text-lg font-medium">Add new student</h2>
                        <button
                            onClick={() => {
                                setShowPopup(false)

                            }}
                            className="text-gray-500"
                        >
                            <span className="material-symbols-outlined text-2xl">
                                close
                            </span>
                        </button>
                    </div>

                    {
                        currentStep === 1 && (
                            <div className="p-4">
                                <p className="text-gray-800 mb-3">
                                    Does your student already have an account?
                                </p>

                                <div className="space-y-2">
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="studentAccount"
                                            value="yes"
                                            checked={isStudentHaveAccount}
                                            onChange={(e) => setIsStudentHaveAccount(true)}
                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>

                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="childAccount"
                                            value="no"
                                            checked={!isStudentHaveAccount}
                                            onChange={(e) => setIsStudentHaveAccount(false)}
                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                            </div>

                        )
                    }

                    {
                        (currentStep === 2 && isStudentHaveAccount) && (
                            <div className="px-6 py-6 space-y-2">
                                <p className="text-gray-800 mb-3">
                                    Please enter your student email.
                                </p>

                                <div>
                                    <input
                                        ref={inputRef}
                                        name="email"
                                        type="email"
                                        placeholder="Email"
                                        required
                                        className="w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                        value={email}
                                        onChange={handleEmailChange}
                                    />
                                </div>
                            </div>

                        )}

                    {
                        (currentStep === 2 && !isStudentHaveAccount) && (
                            <div className="px-6 py-6 space-y-2">
                                <div className="flex justify-between items-center w-full">
                                    <div className="w-[49%]">
                                        <label className="font-medium">First Name*</label>
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            className="w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                        />
                                    </div>

                                    <div className="w-[49%]">
                                        <label className="font-medium">Last Name*</label>
                                        <input
                                            type="text"
                                            placeholder="Last Name"
                                            className="w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <UsernameValidatedComponent
                                    userName={userName}
                                    setUserName={(value) => setUserName(value?.trim())}
                                    getUsername={DoesUserNameExist}
                                    className={{
                                        parentDiv: "",
                                        label: "font-medium",
                                        input: "w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                    }}
                                    touched={usernameTouched}
                                    setTouched={setUsernameTouched}
                                    setIsUserNameExist={setIsUserNameExist}
                                />

                                <EmailValidatedToCheckExistComponent
                                    email={emailAddress}
                                    setEmail={(value) => setEmailAddress(value?.trim())}
                                    getEmail={DoesEmailExist}
                                    className={{
                                        parentDiv: "",
                                        label: "font-medium",
                                        input: "w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                    }}
                                    touched={emailTouched}
                                    setTouched={setEmailTouched}
                                    setIsEmailExist={setIsEmailExist}
                                />

                                <div>
                                    <label className="font-medium">Phone Number*</label>
                                    <input
                                        type='text'
                                        placeholder='Phone No.'
                                        className="w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                        pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(onChangePhoneNumber(e, e.target.value) || "")}
                                    />
                                </div>


                                <PasswordValidation
                                    password={password}
                                    setPassword={(value) => setPassword(value?.trim())}
                                    isValid={isPasswordValid}
                                    setIsValid={setIsPasswordValid}
                                    className={{
                                        parentDiv: "relative",
                                        label: "font-medium",
                                        input: "w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                    }}
                                    touched={passwordTouched}
                                    setTouched={setPasswordTouched}
                                />

                            </div>
                        )}

                    <div className="px-6 py-4 border-t flex justify-between space-x-3">
                        <button
                            className={`px-6 py-2 ${currentStep > 1 ? "bg-primary text-white cursor-pointer" : "bg-gray-300 text-white cursor-not-allowed"}  font-medium rounded`}
                            disabled={currentStep > 1 ? false : true}
                            onClick={handleBackClick}
                        >
                            Back
                        </button>

                        <button
                            className={`px-6 py-2 ${isInvalid() ? "bg-gray-300 text-white cursor-not-allowed" : "bg-primary text-white cursor-pointer"}  font-medium rounded`}
                            disabled={isInvalid()}
                            onClick={handleNextClick}
                        >
                            {currentStep === 2 ? "Invite" : "Next"}
                        </button>
                    </div>
                </div>
            </div >
            <PleasewaitPopup showPopup={showPleasewait} />
        </>
    )
}